import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { ClassificationService, type Classification } from '../classification/ClassificationService';
import { UserLookupService } from '@/features/admin/UserLookupService';
import { GovernanceIssuesService, type GovernanceActivityEntry } from './GovernanceIssuesService';
import { humanizeFlag } from './SensitivityAlerts';

const GOVERNANCE_CATEGORY = 'Governance';
const FEED_LIMIT = 300;

/** One row in the governance activity feed — a triage action or a policy change, unified. */
export interface ActivityRow {
    id: string;
    /** ISO timestamp. */
    when: string;
    /** Who did it (user id / email, or 'system'). */
    actor: string;
    category: 'Triage' | 'Policy';
    /** Human action label, e.g. "Resolved", "Assigned", "Policy updated". */
    action: string;
    /** What it acted on — the issue's policy/flag, or the policy name. */
    target: string;
    /** Extra context (reason, from→to, assignee, filename). */
    detail: string;
}

const TRIAGE_LABEL: Record<string, string> = {
    status_changed: 'Status changed',
    flagged: 'Flagged',
    unflagged: 'Unflagged',
    assigned: 'Assigned',
    unassigned: 'Unassigned',
    resolved: 'Resolved',
    reopened: 'Reopened',
    note_added: 'Note added',
    attachment_added: 'File uploaded',
    turn_dismissed: 'Prompt dismissed',
    turn_restored: 'Prompt restored',
};

function triageDetail(detail: Record<string, unknown>): string {
    if (typeof detail['reason'] === 'string') return detail['reason'];
    if (typeof detail['filename'] === 'string') return detail['filename'];
    if (typeof detail['from'] === 'string' && typeof detail['to'] === 'string') return `${detail['from']} → ${detail['to']}`;
    if (typeof detail['to'] === 'string') return String(detail['to']);
    return '';
}

/** The issue's readable target: policy name for policy issues, humanized flag for sensitivity ones. */
function issueTarget(entry: GovernanceActivityEntry, nameById: Map<string, string>): string {
    const sourceId = entry.issueRef.split(':')[0] ?? '';
    return entry.issueKind === 'policy' ? (nameById.get(sourceId) ?? 'Policy') : humanizeFlag(sourceId);
}

/**
 * Backs the Governance "Activity" tab: a reverse-chronological feed of everything done in
 * governance — triage audit events across all issues (from the context activity endpoint)
 * plus policy changes (governance classifications created/updated).
 */
@injectable()
export class GovernanceActivityViewModel extends BaseViewModel {
    loading = true;
    loaded = false;
    errorMessage: string | null = null;
    rows: ActivityRow[] = [];

    constructor(
        private readonly issuesService: GovernanceIssuesService,
        private readonly classifications: ClassificationService,
        private readonly userLookup: UserLookupService,
    ) {
        super();
    }

    async initialize(): Promise<void> {
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            const [activity, all] = await Promise.all([
                this.issuesService.activity(FEED_LIMIT),
                this.classifications.list(),
                this.userLookup.ready(),
            ]);
            const nameById = new Map(all.map((c) => [c.id, c.name]));
            const resolveActor = (actor: string): string => this.userLookup.emailFor(actor);
            const triage = activity.map(
                (e): ActivityRow => ({
                    id: e.id,
                    when: e.createdAt,
                    actor: resolveActor(e.actorUserId),
                    category: 'Triage',
                    action: TRIAGE_LABEL[e.eventKind] ?? e.eventKind,
                    target: issueTarget(e, nameById),
                    detail: triageDetail(e.detail as Record<string, unknown>),
                }),
            );
            const policy = all.filter((c) => c.category === GOVERNANCE_CATEGORY).flatMap((c) => policyRows(c, resolveActor));
            this.rows = [...triage, ...policy].sort((a, b) => b.when.localeCompare(a.when)).slice(0, FEED_LIMIT);
        } catch (error) {
            this.errorMessage = error instanceof Error ? error.message : 'Failed to load activity';
            this.rows = [];
        } finally {
            this.loading = false;
            this.loaded = true;
            this.notifyChange();
        }
    }
}

/** Created (and, if edited, updated) rows for a governance policy. */
function policyRows(c: Classification, resolveActor: (actor: string) => string): ActivityRow[] {
    const rows: ActivityRow[] = [
        { id: `${c.id}:created`, when: c.createdAt, actor: resolveActor(c.createdBy), category: 'Policy', action: 'Policy created', target: c.name, detail: '' },
    ];
    if (c.updatedAt !== c.createdAt) {
        rows.push({ id: `${c.id}:updated`, when: c.updatedAt, actor: resolveActor(c.updatedBy), category: 'Policy', action: 'Policy updated', target: c.name, detail: '' });
    }
    return rows;
}
